import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter
} from '@angular/core';
import { FlyoutType, MenuPanelItem } from '../../../domain/menu.domain';

/**
 * The Menu Item Component
 *
 * This component displays the menu item
 */
@Component({
  selector: 'td-wb-menu-item',
  templateUrl: './item.component.html',
  styles: []

})
export class MenuItemComponent implements OnChanges {

  /**
   * Menu Item
   */
  @Input() menuItem: MenuPanelItem;

  /**
   * Active Item ID
   */
  @Input() activeItemId: string;

  @Input() flyoutType: FlyoutType;

  @Output() readonly selected: EventEmitter<MenuPanelItem> = new EventEmitter<MenuPanelItem>();

  @Output() readonly openChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() readonly activeChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Class for item container
   */
  private itemClass: string;

  /**
   * Class for overlay class
   */
  private itemOverlayClass: string;

  /**
   * Class for opened item class
   */
  private itemOpenClass: string;

  /**
   * Class for active item
   */
  private activeItemClass: string;

  /**
   * Boolean for determing if item is opened
   */
  private open: boolean = false;

  /**
   * Boolean for determing if item is active
   */
  private active: boolean = false;

  /**
   * This function updates active item id
   *
   * @param changes changes from input
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeItemId !== undefined && changes.activeItemId.currentValue !== undefined) {
      this.activeItemId = changes.activeItemId.currentValue;
    }
    this.setActive();
    if (changes.menuItem !== undefined && changes.menuItem.currentValue !== undefined) {
      this.setOpen(this.menuItem.open);
    }
    if (changes.flyoutType !== undefined && changes.flyoutType.currentValue !== undefined) {
      this.itemClass = 'td-wb-nav__' + this.flyoutType + '__item';
      this.itemOverlayClass = this.hasSubMenu()
        ? 'td-wb-nav__' + this.flyoutType + '__overlay'
        : '';
      this.itemOpenClass = 'td-wb-nav__' + this.flyoutType + '__item--active';
      this.activeItemClass = 'td-wb-nav__' + this.flyoutType + '__item--current';
    }
  }

  /**
   * This function closes the flyout that contains menu item
   *
   * @param event Mouse click event
   */
  close(): void {
    this.setOpen(false);
  }

  /**
   * This function returns whether flyout containing menu item is opened
   *
   * @returns Returns true if flyout is opened. Otherwise, false.
   */
  isOpen(): boolean {
    return this.open;
  }

  /**
   * This function updates the open state of flyout menu
   *
   * @returns Returns true if menu item is opened. Otherwise, false.
   */
  setOpen(open: boolean = false): void {
    if (this.open !== open) {
      this.open = open;
      if (this.menuItem !== undefined && this.menuItem.open !== this.open) {
        this.menuItem.open = this.open;
        if (this.menuItem.open) {
          this.selected.emit(this.menuItem);
        }
      }
      this.openChange.emit(this.open);
    }
  }

  /**
   * This function toggles the open state of flyout menu
   *
   * @param open Boolean to open/close flyout.
   */
  toggle(): void {
    this.setOpen(!this.open);
  }

  /**
   * This function returns whether current item is active
   *
   * @returns Returns true if current item is active. Otherwise, false.
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * This function gets classes for menu item container
   *
   * @returns Classes for menu item.
   */
  getClasses(): string {
    const result = [this.itemClass, this.itemOverlayClass, 'td-wb-menu-item'];
    if (this.active) {
      result.push(this.activeItemClass);
    }
    if (this.open) {
      result.push(this.itemOpenClass);
    }
    return result.join(' ');
  }

  /**
   * This function returns whether menu item has active item
   *
   * @returns Returns true if menu item has active item. Otherwise, false.
   */
  hasActiveItem(): boolean {
    return (
      this.activeItemId !== undefined &&
      this.menuItem !== undefined &&
      this.menuItem.id === this.activeItemId
    );
  }

  /**
   * This function returns whether menu item has sub menu
   *
   * @returns Returns true if menu item has sub menu. Otherwise, false.
   */
  private hasSubMenu(): boolean {
    return (
      this.menuItem !== undefined &&
      this.menuItem.menu !== undefined &&
      this.menuItem.menu[0].item !== undefined
    );
  }

  private setActive(): void {
    const isActiveItem = this.hasActiveItem();
    if ( this.active !== isActiveItem) {
      this.active = isActiveItem;
      this.activeChange.emit(this.active);
    }
  }
}
